// Weekly volume trend chart model shared by the analytics views.
//
// Turns the API's ISO-week buckets into chart.js data/options with three rules
// that every trend chart in the app must follow:
//   1. Axis labels are dates, not ISO week numbers ("27 Jul – 2 Aug"; for long
//      ranges, a month name where a month starts) — matching the PDF report.
//   2. Weeks only partly inside the selected range are *partial*: their total is
//      legitimately low, so they are drawn dashed and starred, never as a trend.
//   3. Colours derive from the brand token — no second hard-coded green.
//
// Usage: const trend = useWeeklyTrend(weeklyRef, startDate, endDate, theme, chartColors)
//   weeklyRef: Ref<Array<{ week: '2026-W31', actualVolume: number, ... }>>
import { computed } from 'vue'
import { lineChartOptions } from '@/utils/chartOptions'
import { formatWeekRangeLabel, weekCoverage, isoWeekRange } from '@/utils/format'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LONG_RANGE_WEEKS = 12 // above this, per-week range labels no longer fit

// Month label when a week contains the 1st of a month (the first week always gets one).
function monthTick(week, isFirst) {
  const r = isoWeekRange(week)
  if (!r) return ''
  for (let d = new Date(r.start); d <= r.end; d.setDate(d.getDate() + 1)) {
    if (d.getDate() === 1) return MONTHS[d.getMonth()]
  }
  return isFirst ? MONTHS[r.start.getMonth()] : ''
}

export function useWeeklyTrend(weeklyRef, startDate, endDate, theme, chartColors, { valueKey = 'actualVolume', valueSuffix = ' L', projectionRef = null } = {}) {
  const weekly = computed(() => weeklyRef.value || [])
  const hasTrend = computed(() => weekly.value.length >= 1)
  // Optional month-end projection: [{ week, value }] for weeks AFTER the last actual one.
  // Drawn as a dotted neutral line continuing from the last real point.
  const projected = computed(() => (projectionRef && projectionRef.value) || [])

  const coverage = computed(() => weekly.value.map((w) => weekCoverage(w.week, startDate.value, endDate.value)))
  const partialWeeks = computed(() => coverage.value.map((c, i) => (c.partial ? i : -1)).filter((i) => i >= 0))
  const partialWeekSummary = computed(() =>
    partialWeeks.value
      .map((i) => `${coverage.value[i].days} day${coverage.value[i].days === 1 ? '' : 's'}`)
      .join(' and ')
  )

  const isLongRange = computed(() => weekly.value.length > LONG_RANGE_WEEKS)

  const rangeLabels = computed(() => weekly.value.map((w) => formatWeekRangeLabel(w.week)))
  const projectedLabels = computed(() => projected.value.map((p) => formatWeekRangeLabel(p.week)))
  const tickLabels = computed(() => {
    const partial = new Set(partialWeeks.value)
    let actual
    if (isLongRange.value) {
      actual = weekly.value.map((w, i) => {
        const m = monthTick(w.week, i === 0)
        return m ? m + (partial.has(i) ? ' *' : '') : partial.has(i) ? '*' : ''
      })
    } else {
      actual = rangeLabels.value.map((l, i) => l + (partial.has(i) ? ' *' : ''))
    }
    const proj = projected.value.map((p) => (isLongRange.value ? monthTick(p.week, false) : formatWeekRangeLabel(p.week)))
    return [...actual, ...proj]
  })

  const trendData = computed(() => {
    const partial = new Set(partialWeeks.value)
    const actual = weekly.value.map((w) => w[valueKey] ?? 0)
    const n = actual.length
    const datasets = [
      {
        label: 'Actual Volume',
        data: [...actual, ...projected.value.map(() => null)],
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primary + '1F', // ~12% brand green
        borderWidth: 2,
        fill: true,
        tension: 0.35,
        pointRadius: isLongRange.value ? 2 : 3,
        pointBackgroundColor: chartColors.primary,
        // Segments touching a partial week are dashed so the edge dip reads as "incomplete", not "dropped".
        segment: {
          borderDash: (ctx) => (partial.has(ctx.p0DataIndex) || partial.has(ctx.p1DataIndex) ? [6, 4] : undefined),
        },
      },
    ]
    if (n && projected.value.length) {
      datasets.push({
        label: 'Projected',
        // null up to the last actual point, which is repeated so the dotted line continues from it
        data: [...Array(n - 1).fill(null), actual[n - 1], ...projected.value.map((p) => p.value)],
        borderColor: theme.value.axisColor,
        borderWidth: 2,
        borderDash: [3, 4],
        fill: false,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointBackgroundColor: theme.value.axisColor,
      })
    }
    return { labels: [...rangeLabels.value, ...projectedLabels.value], datasets }
  })

  const trendOptions = computed(() => {
    const opts = lineChartOptions(theme.value, { valueSuffix })
    const cov = coverage.value
    const ticks = tickLabels.value
    const n = weekly.value.length
    opts.scales.x.ticks = {
      ...opts.scales.x.ticks,
      autoSkip: !isLongRange.value,
      maxRotation: 0,
      callback: (_value, index) => ticks[index] ?? '',
    }
    // Hide the duplicated join point and null gaps from the tooltip.
    opts.plugins.tooltip.filter = (item) => item.parsed.y != null && !(item.datasetIndex === 1 && item.dataIndex < n)
    opts.plugins.tooltip.callbacks.title = (items) => {
      const i = items[0]?.dataIndex ?? 0
      if (i >= n) return `Projected · week of ${projectedLabels.value[i - n] || ''}`
      const base = `Week of ${rangeLabels.value[i] || ''}`
      const c = cov[i]
      return c?.partial ? `${base} (partial: ${c.days} of 7 days in range)` : base
    }
    return opts
  })

  return { hasTrend, coverage, partialWeeks, partialWeekSummary, isLongRange, trendData, trendOptions }
}
